import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { AppleWalletBackField } from '../../../api'
import Button from '../../UI/Button/Button'
import AppleWalletHtmlEditor from '../../UI/PassHtmlEditor/AppleWalletHtmlEditor'
import { AdFormSchema } from '../AdForm/AdFormTypes'

const AppleWalletBackFields = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<AdFormSchema>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passDetails.appleWalletBackFields',
  })

  const addField = () => {
    const newField: AppleWalletBackField = {
      key: `field_${Date.now()}`,
      label: '',
      value: '',
      sortOrder: fields.length,
    }
    append(newField)
  }

  const moveUp = (index: number) => {
    if (index > 0) {
      const currentFields = watch('passDetails.appleWalletBackFields') || []
      const newFields = [...currentFields]

      // Swap with previous
      ;[newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]]

      // Update sortOrder values
      newFields.forEach((field, idx) => {
        field.sortOrder = idx
      })

      setValue('passDetails.appleWalletBackFields', newFields)
    }
  }

  const moveDown = (index: number) => {
    if (index < fields.length - 1) {
      const currentFields = watch('passDetails.appleWalletBackFields') || []
      const newFields = [...currentFields]

      // Swap with next
      ;[newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]]

      // Update sortOrder values
      newFields.forEach((field, idx) => {
        field.sortOrder = idx
      })

      setValue('passDetails.appleWalletBackFields', newFields)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Apple Wallet Back Fields</h3>
        <Button type="outline" onClick={addField} className="text-sm">
          + Add Field
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p>No back fields added yet.</p>
          <p className="text-sm">Click &quot;Add Field&quot; to create your first label-value pair.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Field {field.sortOrder + 1}</span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-1 py-0.5 rounded" title="Sort Order">
                    #{field.sortOrder}
                  </span>

                  {/* Reorder buttons */}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(index)}
                      disabled={index === fields.length - 1}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                >
                  Remove
                </button>
              </div>

              {/* Label Input */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-600 mb-2">Label</label>
                <Controller
                  control={control}
                  name={`passDetails.appleWalletBackFields.${index}.label`}
                  // rules={{ required: 'Label is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter field label (e.g., Terms & Conditions, Store Hours)"
                      className={`w-full p-3 bg-white border ${
                        errors.passDetails?.appleWalletBackFields?.[index]?.label ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition duration-200`}
                    />
                  )}
                />
                {errors.passDetails?.appleWalletBackFields?.[index]?.label && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.passDetails?.appleWalletBackFields?.[index]?.label?.message}
                  </p>
                )}
              </div>

              {/* Value HTML Editor */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2">Content</label>
                <Controller
                  control={control}
                  name={`passDetails.appleWalletBackFields.${index}.value`}
                  rules={{ required: 'Content is required' }}
                  render={({ field: { onChange, value } }) => (
                    <AppleWalletHtmlEditor
                      value={value || ''}
                      onChange={onChange}
                      placeholder="Enter the content for this field..."
                    />
                  )}
                />
                {errors.passDetails?.appleWalletBackFields?.[index]?.value && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.passDetails?.appleWalletBackFields?.[index]?.value?.message}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AppleWalletBackFields
